import requestUtils from 'api/requestUtils'
import { paths } from '@luxuryescapes/contract-business'
import { getCartItems } from 'api/cart'
import { getOfferById } from 'api/offer'
import { journeyV2Map } from 'api/lib/journeyV2Map'
import { Models } from '@luxuryescapes/lib-flights-types'

type ApprovalRequestResponse = paths['/api/business-traveller/businesses/{businessId}/employee/{employeeId}/approval-request']['get']['responses'][200]['content']['application/json']

export enum APPROVE_ORDER_REQUEST_GET_ERRORS {
  NOT_FOUND = 'Approval request not found!',
  OTHER = 'Something went wrong!',
}

export function mapApprovalRequest(
  request: ApprovalRequestResponse['result'][number],
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
  hotelItems?: Array<App.BusinessTraveller.HotelItem>,
): App.BusinessTraveller.ApprovalRequest {
  const requester = employees.find((employee) => employee.id === request.requesterId)
  const mappedApprovalRequest: App.BusinessTraveller.ApprovalRequest = {
    ...request,
    details: { flightItems: request.type === 'flight' ? (request.details?.flightItems as Array<Models.Search.Fare>).map(item => journeyV2Map(item)) : undefined },
    requesterName: requester ? `${requester.firstName} ${requester.lastName}` : '',
    hotelItems,
  }
  return mappedApprovalRequest
}

interface Params {
  businessId: string;
  channelMarkup?: App.ChannelMarkup
  currentRegionCode: string;
  employeeId: string;
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>;
}

async function getApprovalRequestsForApprover(params: Params) {
  const { businessId, employeeId, employees, currentRegionCode, channelMarkup } = params

  const queryParams = '?role=approver'

  try {
    const approvalResponse = await requestUtils.get<ApprovalRequestResponse>(
      `/api/business-traveller/businesses/${businessId}/employee/${employeeId}/approval-request${queryParams}`,
      { credentials: 'include' },
    )

    const mappedRequests = await Promise.all(approvalResponse.result.map(async(request) => {
      if (request.type === 'flight') {
        return mapApprovalRequest(request, employees)
      }

      try {
        const cartItems = await getCartItems(request.cartId)

        // Need to get the missing offer information that isn't included in the cart item.
        const hotelItems: Array<App.BusinessTraveller.HotelItem> = []
        for (const cartItem of cartItems.items) {
          if ('offerId' in cartItem &&
        (cartItem.itemType === 'bedbankHotel' || cartItem.itemType === 'hotel')
          ) {
            const offer: any = await getOfferById(cartItem.offerId, {
              region: currentRegionCode,
              channelMarkup,
            })

            const location = offer.locationHeading ?
          `${offer.locationHeading},  ${offer.locationSubheading}` :
          `${offer.property?.address?.city},  ${offer.property?.address?.stateProvinceName}`

            const hotelItem: App.BusinessTraveller.HotelItem = {
              id: cartItem.offerId,
              name: offer.name,
              location,
              image: offer.image.id,
              checkIn: 'checkIn' in cartItem ? cartItem.checkIn : '',
              checkOut: 'checkOut' in cartItem ? cartItem.checkOut : '',
              duration: cartItem.duration,
            }

            hotelItems.push(hotelItem)
          }
        }

        return mapApprovalRequest(request, employees, hotelItems)
      } catch
      {
        // skip missing carts
        return undefined
      }
    }))

    return mappedRequests.filter(Boolean)
  } catch (error: any) {
    if (error?.status === 404) {
      return Promise.reject(APPROVE_ORDER_REQUEST_GET_ERRORS.NOT_FOUND)
    }
    return Promise.reject(APPROVE_ORDER_REQUEST_GET_ERRORS.OTHER)
  }
}

export default getApprovalRequestsForApprover
